'use no memo';
import { Requisition } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  FormItem,
  FormMessage,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kalos/ui';
import { ArrowRightIcon, ExternalLinkIcon } from '@radix-ui/react-icons';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { type ColumnDef, getCoreRowModel } from '@tanstack/react-table';
import { useReactTable } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { ROWS_PER_PAGE } from '../../../constants';
import { Flagged } from '../../../featureFlags/Flagged';
import { useFeatureFlag } from '../../../featureFlags/useFeatureFlag';
import { TechniciansComboboxPicker } from '../../ComponentsLibrary/Pickers/newPickers/TechnicianComboboxPicker';
import {
  type FulfillmentItemRow,
  isEnteredByAdmin,
  requisitionTotalActualCost,
  toolRequestStatusLabel,
  transformFulfillmentItems,
} from './employeeToolLog.utils';
import { RequisitionFormDialog } from './RequisitionFormDialog';
import { ToolFundCatalogDialog } from './ToolFundCatalogDialog';
import { ToolFundEmployeeAssetsTable } from './ToolFundEmployeeAssetsTable';
import {
  TOOL_FUND_TRAINING_COURSE_URL,
  useEmployeeToolLog,
  useReassignRequestForm,
  useToolFundTrainingConfirmation,
} from './useEmployeeToolLog';

const LOADING_REQUISITIONS: Requisition[] = Array.from({ length: 5 }, (_, idx) =>
  Requisition.create({
    id: idx,
    userId: 0,
    createdBy: 0,
    statusId: 0,
    requestDate: '',
    totalEstimatedCost: 0,
    items: [],
  }),
);

// Component for rendering fulfillment items in accordion
const FulfillmentItemsTable = ({
  items,
  columns,
}: {
  items: FulfillmentItemRow[];
  columns: ColumnDef<FulfillmentItemRow>[];
}) => {
  const subTable = useReactTable<FulfillmentItemRow>({
    data: items,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    enableSorting: false,
  });

  return <DataTable table={subTable} showHeader={true} />;
};

interface ToolFundRequestsProps {
  userId: number;
}

const ReassignDialog = ({
  requisition,
  onOpenChange,
}: {
  requisition: Requisition;
  onOpenChange?: (open: boolean) => void;
}) => {
  const { form, handleOpenChange, handleReassign, requisitionUpdateMutation } =
    useReassignRequestForm({ requisition, onOpenChange });

  return (
    <Dialog open={true} onOpenChange={handleOpenChange} modal>
      <DialogContent
        onPointerDownOutside={(e) => {
          // Allow closing by clicking outside unless mutation is pending
          if (requisitionUpdateMutation.isPending) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Allow closing with Escape key unless mutation is pending
          if (requisitionUpdateMutation.isPending) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Reassign Request</DialogTitle>
          <DialogDescription>
            Select an employee to reassign this tool request to. This will change who is credited
            with creating the request.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleReassign)} className="space-y-4">
          <Form {...form}>
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <TechniciansComboboxPicker
                    onSelect={(u) => field.onChange(u?.id ?? 0)}
                    selected={field.value.toString()}
                    disabled={field.disabled}
                    emptyLabel="Select an employee to reassign to"
                    placeholder="Search Employee..."
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex flex-col gap-2">
              <Button
                variant="outline"
                type="button"
                disabled={requisitionUpdateMutation.isPending}
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={requisitionUpdateMutation.isPending}
                isLoading={requisitionUpdateMutation.isPending}
              >
                Reassign
              </Button>
            </DialogFooter>
          </Form>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TrainingConfirmationDialog = ({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { handleCancel, handleConfirm, isPending } = useToolFundTrainingConfirmation({
    onOpenChange,
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tool Fund Training Required</DialogTitle>
          <DialogDescription className="space-y-2">
            <p>
              In order to participate in the Tool Fund, you must complete the required training in
              the Learning Management System (LMS). Select{' '}
              <a
                href={TOOL_FUND_TRAINING_COURSE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                the following link
              </a>
              . Sign in as employee using Google sign in, login to the LMS, and complete the Tool
              Fund training.
            </p>
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} isLoading={isPending}>
            Confirm <ExternalLinkIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EmployeeToolLog = ({ userId }: ToolFundRequestsProps) => {
  const effectiveUserId = userId;
  const [activeTab, setActiveTab] = useState('allRequests');
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState<Requisition | null>(null);

  const getStatusBadge = (statusId: number, isRequisitionLevel = false) => {
    switch (statusId) {
      case 1:
        return (
          <span className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-900">
            {toolRequestStatusLabel(statusId, isRequisitionLevel)}
          </span>
        );
      case 2:
        return (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            {toolRequestStatusLabel(statusId, isRequisitionLevel)}
          </span>
        );
      case 3:
        return isRequisitionLevel ? (
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
            {toolRequestStatusLabel(statusId, isRequisitionLevel)}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
            {toolRequestStatusLabel(statusId, isRequisitionLevel)}
          </span>
        );
      case 4:
        return (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
            {toolRequestStatusLabel(statusId, isRequisitionLevel)}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
            {toolRequestStatusLabel(statusId, isRequisitionLevel)}
          </span>
        );
    }
  };

  const {
    page,
    setPage,
    availableBalance,
    pendingRequestsQuery: { data: requestsData, isLoading: isLoadingRequests },
    allEmployeeRequestsQuery: {
      data: allEmployeeRequestsData,
      isLoading: isLoadingAllEmployeeRequests,
    },
    allManagerRequestsQuery: {
      data: allManagerRequestsData,
      isLoading: isLoadingAllManagerRequests,
    },
    techniciansData,
    isToolFundAdmin,
    isManager,
    isOwnLog,
    categoriesQuery,
    toolFundSettings,
    isEligibleForToolFund,
    ineligibleLabel,
    isLoading,
  } = useEmployeeToolLog({ userId: effectiveUserId });
  const isAdminPurchaseOn = useFeatureFlag('tool-fund.admin-purchase');

  // Main table columns for requisitions
  const requisitionColumns: ColumnDef<Requisition>[] = useMemo(
    () => [
      {
        id: 'requestNumber',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Request #" />
        ),
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2">
            {row.original.id}
            {isAdminPurchaseOn && isEnteredByAdmin(row.original) && (
              <Flagged flag="tool-fund.admin-purchase">
                <Badge variant="secondary">Entered by admin</Badge>
              </Flagged>
            )}
          </span>
        ),
      },
      {
        id: 'requestDate',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Request Date" />
        ),
        cell: ({ row }) => {
          return format(parseISO(row.original.requestDate), 'MM/dd/yyyy');
        },
      },
      {
        id: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Status" />
        ),
        cell: ({ row }) => {
          return getStatusBadge(row.original.statusId, true);
        },
      },
      {
        id: 'requestedFor',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Requested For" />
        ),
        cell: ({ row }) => {
          const user = techniciansData?.get(row.original.userId);
          return user ? `${user.firstname} ${user.lastname}` : 'Unknown User';
        },
      },
      {
        id: 'numberOfItems',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Number of Items" />
        ),
        cell: ({ row }) => {
          return row.original.items?.length || 0;
        },
      },
      {
        id: 'totalCost',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Total Cost" />
        ),
        cell: ({ row }) => {
          const totalActualCost = requisitionTotalActualCost(row.original);
          return `$${totalActualCost.toFixed(2)}`;
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2">
              {isToolFundAdmin && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setReassignDialogOpen(row.original)}
                      >
                        <ArrowRightIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Reassign Request</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        },
      },
    ],
    [isToolFundAdmin, techniciansData, isAdminPurchaseOn],
  );

  // Sub table columns for fulfillment items
  const fulfillmentItemColumns: ColumnDef<FulfillmentItemRow>[] = useMemo(
    () => [
      {
        id: 'toolName',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Tool Name" />
        ),
        cell: ({ row }) => {
          return row.original.toolName;
        },
      },
      {
        id: 'category',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Category" />
        ),
        cell: ({ row }) => {
          const category = categoriesQuery.data?.results?.find(
            (cat) => cat.id === row.original.categoryId,
          )?.name;
          return category || 'N/A';
        },
      },
      {
        id: 'quantity',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Quantity" />
        ),
        cell: ({ row }) => {
          return row.original.quantity;
        },
      },
      {
        id: 'statusDesc',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Status" />
        ),
        cell: ({ row }) => {
          return getStatusBadge(row.original.status);
        },
      },
      {
        id: 'estimatedCost',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Estimated Cost" />
        ),
        cell: ({ row }) => {
          return `$${row.original.estimatedCost.toFixed(2)}`;
        },
      },
      {
        id: 'actualCost',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Actual Cost" />
        ),
        cell: ({ row }) => {
          return row.original.status === 3
            ? `$${Number(0).toFixed(2)}`
            : row.original.actualCost
              ? `$${row.original.actualCost.toFixed(2)}`
              : 'N/A';
        },
      },
      {
        id: 'comments',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Comments" />
        ),
        cell: ({ row }) => {
          return row.original.notes || '-';
        },
      },
    ],
    [categoriesQuery.data?.results],
  );

  // Create tables for each tab
  const pendingRequestsTable = useReactTable<Requisition>({
    data: isLoadingRequests ? LOADING_REQUISITIONS : requestsData?.results ?? [],
    columns: requisitionColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    enableSorting: false,
  });

  const allEmployeeRequestsTable = useReactTable<Requisition>({
    data: isLoadingAllEmployeeRequests
      ? LOADING_REQUISITIONS
      : allEmployeeRequestsData?.results ?? [],
    columns: requisitionColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    enableSorting: false,
  });

  const allManagerRequestsTable = useReactTable<Requisition>({
    data: isLoadingAllManagerRequests
      ? LOADING_REQUISITIONS
      : allManagerRequestsData?.results ?? [],
    columns: requisitionColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    enableSorting: false,
  });

  const pageCount = {
    pending:
      (requestsData?.results?.length || 0) > 0
        ? Math.ceil((requestsData?.results?.length || 0) / ROWS_PER_PAGE)
        : 0,
    allEmployeeRequests:
      (allEmployeeRequestsData?.results?.length || 0) > 0
        ? Math.ceil((allEmployeeRequestsData?.results?.length || 0) / ROWS_PER_PAGE)
        : 0,
    allManagerRequests:
      (allManagerRequestsData?.results?.length || 0) > 0
        ? Math.ceil((allManagerRequestsData?.results?.length || 0) / ROWS_PER_PAGE)
        : 0,
  };

  useEffect(() => {
    if (isOwnLog && toolFundSettings && toolFundSettings.trainingConfirmed === false) {
      setShowTrainingDialog(true);
    }
  }, [toolFundSettings, isOwnLog]);

  return (
    <>
      <Card className="max-w-[100vw] px-0 pt-6 sm:pt-0">
        <CardHeader className="pt-4">
          <div className="grid grid-cols-3 items-center">
            <CardTitle>Employee Tool Log</CardTitle>
            <p className="text-muted-foreground text-center text-sm font-medium">
              Available Balance
            </p>
            <div></div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-4">
            <div className="grid grid-cols-3 items-center px-6 pb-4">
              <div className="flex flex-col gap-2 md:flex-row">
                <ToolFundCatalogDialog
                  trigger={
                    <Button size="sm" variant="outline" disabled={isLoading}>
                      View Tool Catalog
                    </Button>
                  }
                />
                {isLoading ? (
                  <Button size="sm" disabled>
                    <Skeleton className="h-4 w-24" />
                  </Button>
                ) : isEligibleForToolFund ? (
                  <RequisitionFormDialog userId={effectiveUserId} />
                ) : (
                  <Button size="sm" disabled>
                    {ineligibleLabel}
                  </Button>
                )}
              </div>
              <div className="text-center">
                {isLoading ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <h2 className="text-2xl font-bold">${availableBalance.toFixed(2)}</h2>
                )}
              </div>
              <div></div>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(tab) => {
              setActiveTab(tab);
              setPage(0);
            }}
          >
            <TabsList>
              <TabsTrigger value="allRequests" disabled={isLoading}>
                All Requests
              </TabsTrigger>
              {!isManager && (
                <TabsTrigger value="pending" disabled={isLoading}>
                  Pending Requests
                </TabsTrigger>
              )}
              <TabsTrigger value="tools" disabled={isLoading}>
                Tools
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="max-w-full space-y-4 overflow-auto p-4">
              {!isManager &&
                (isLoadingRequests ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <>
                    {pageCount.pending > 1 && (
                      <div className="flex items-center justify-end pr-4">
                        <div className="flex w-full max-w-max items-center justify-between gap-4">
                          <DataTablePagination
                            currentPage={page}
                            setPage={setPage}
                            pageCount={pageCount.pending}
                          />
                          {(requestsData?.results?.length || 0) > 0 && (
                            <p>Total Entries: {requestsData?.results?.length || 0}</p>
                          )}
                        </div>
                      </div>
                    )}
                    <DataTable
                      table={pendingRequestsTable}
                      enableAccordion={true}
                      accordionProps={{
                        className: 'h-0 min-h-0',
                        contentClassName: 'p-0 m-0',
                        iconClassName: 'h-0 min-h-0',
                      }}
                      accordionContent={(row) => {
                        const items = transformFulfillmentItems(row.original);
                        if (items.length === 0) return null;

                        return (
                          <FulfillmentItemsTable items={items} columns={fulfillmentItemColumns} />
                        );
                      }}
                    />
                  </>
                ))}
            </TabsContent>

            <TabsContent value="allRequests" className="max-w-full space-y-6 p-4">
              {/* Employee Requests Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Employee Requests</h3>
                <div className="max-h-96 overflow-auto rounded-md border">
                  {isLoadingAllEmployeeRequests ? (
                    <div className="space-y-2 p-4">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ) : (
                    <>
                      <DataTable
                        table={allEmployeeRequestsTable}
                        enableAccordion={true}
                        accordionProps={{
                          className: 'accordion-row',
                          contentClassName: 'accordion-content',
                          iconClassName: 'accordion-icon',
                        }}
                        accordionContent={(row) => {
                          const items = transformFulfillmentItems(row.original);
                          if (items.length === 0) return null;

                          return (
                            <FulfillmentItemsTable items={items} columns={fulfillmentItemColumns} />
                          );
                        }}
                      />
                      {pageCount.allEmployeeRequests > 1 && (
                        <div className="bg-muted/50 border-t p-4">
                          <div className="flex items-center justify-between">
                            <DataTablePagination
                              currentPage={page}
                              setPage={setPage}
                              pageCount={pageCount.allEmployeeRequests}
                            />
                            {(allEmployeeRequestsData?.results?.length || 0) > 0 && (
                              <p className="text-muted-foreground text-sm">
                                Total Entries: {allEmployeeRequestsData?.results?.length || 0}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Manager Requests Section */}
              {!isManager && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Manager Requests</h3>
                  <div className="max-h-96 overflow-auto rounded-md border">
                    {isLoadingAllManagerRequests ? (
                      <div className="space-y-2 p-4">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    ) : (
                      <>
                        <DataTable
                          table={allManagerRequestsTable}
                          enableAccordion={true}
                          accordionProps={{
                            className: 'accordion-row',
                            contentClassName: 'accordion-content',
                            iconClassName: 'accordion-icon',
                          }}
                          accordionContent={(row) => {
                            const items = transformFulfillmentItems(row.original);
                            if (items.length === 0) return null;

                            return (
                              <FulfillmentItemsTable
                                items={items}
                                columns={fulfillmentItemColumns}
                              />
                            );
                          }}
                        />
                        {pageCount.allManagerRequests > 1 && (
                          <div className="bg-muted/50 border-t p-4">
                            <div className="flex items-center justify-between">
                              <DataTablePagination
                                currentPage={page}
                                setPage={setPage}
                                pageCount={pageCount.allManagerRequests}
                              />
                              {(allManagerRequestsData?.results?.length || 0) > 0 && (
                                <p className="text-muted-foreground text-sm">
                                  Total Entries: {allManagerRequestsData?.results?.length || 0}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="tools" className="max-w-full space-y-4 overflow-auto p-4">
              <ToolFundEmployeeAssetsTable userId={effectiveUserId} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      <TrainingConfirmationDialog
        isOpen={showTrainingDialog}
        onOpenChange={setShowTrainingDialog}
      />
      {reassignDialogOpen && (
        <ReassignDialog
          requisition={reassignDialogOpen}
          onOpenChange={(open) => !open && setReassignDialogOpen(null)}
        />
      )}
    </>
  );
};
